import React, { useState } from 'react';
import { ApiService } from '../services/api';

interface ImportQuestionsProps {
  sessionId: string;
  onImportSuccess: () => void;
  onCancel: () => void;
}

const ImportQuestions: React.FC<ImportQuestionsProps> = ({ sessionId, onImportSuccess, onCancel }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<any>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/json') {
        setError('Veuillez sélectionner un fichier JSON');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setError('Le fichier est trop volumineux (max 5MB)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    setError(null);
    setImportResult(null);

    try {
      const result = await ApiService.importQuestions(sessionId, selectedFile);
      setImportResult(result);
      
      if (result.success) {
        // Recharger les questions après un délai
        setTimeout(() => {
          onImportSuccess();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await ApiService.downloadTemplate();
    } catch (err: any) {
      console.error('Erreur téléchargement API:', err);
      // Téléchargement de secours depuis le dossier public
      try {
        const response = await fetch('/ciar-questions-template.json');
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ciar-questions-template.json';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          setError('Erreur lors du téléchargement du modèle (fallback échoué)');
        }
      } catch (fallbackErr) {
        setError(err.message || 'Erreur lors du téléchargement du modèle');
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>📥 Importer des questions depuis JSON</h3>
          <button 
            className="btn-close" 
            onClick={onCancel}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {!importResult ? (
            <>
              {/* Instructions */}
              <div className="mb-3 p-3" style={{ 
                background: '#e3f2fd', 
                borderRadius: '8px',
                border: '1px solid #bbdefb'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#1565c0' }}>
                  📋 Instructions
                </h4>
                <ol style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>Téléchargez le modèle JSON pour voir le format</li>
                  <li>Préparez vos questions selon ce modèle</li>
                  <li>Sélectionnez votre fichier JSON</li>
                  <li>Cliquez sur "Importer"</li>
                </ol>
              </div>

              {/* Téléchargement du modèle */}
              <div className="mb-3">
                <button 
                  className="btn btn-outline"
                  onClick={handleDownloadTemplate}
                  style={{ width: '100%' }}
                >
                  📄 Télécharger le modèle JSON
                </button>
              </div>

              {/* Sélection de fichier */}
              <div className="form-group">
                <label className="form-label">
                  Sélectionner un fichier JSON
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  className="form-input"
                  disabled={loading}
                />
                <small className="form-help">
                  Format accepté: JSON (max 5MB)
                </small>
              </div>

              {/* Erreurs */}
              {error && (
                <div className="mb-3 p-2" style={{ 
                  background: '#f8d7da', 
                  color: '#721c24', 
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb'
                }}>
                  ❌ {error}
                </div>
              )}

              {/* Fichier sélectionné */}
              {selectedFile && (
                <div className="mb-3 p-2" style={{ 
                  background: '#d4edda', 
                  color: '#155724', 
                  borderRadius: '4px',
                  border: '1px solid #c3e6cb'
                }}>
                  📁 Fichier sélectionné: {selectedFile.name}
                  <br />
                  <small>Taille: {(selectedFile.size / 1024).toFixed(1)} KB</small>
                </div>
              )}
            </>
          ) : (
            /* Résultats de l'import */
            <div>
              <div className="mb-3 p-3" style={{ 
                background: importResult.success ? '#d4edda' : '#f8d7da', 
                color: importResult.success ? '#155724' : '#721c24',
                borderRadius: '8px',
                border: `1px solid ${importResult.success ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>
                  {importResult.success ? '✅ Import réussi!' : '❌ Import échoué'}
                </h4>
                <p style={{ margin: 0 }}>
                  {importResult.message}
                </p>
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Statistiques:</strong><br />
                  • Importées: {importResult.imported}/{importResult.total} questions
                  {importResult.errors && importResult.errors.length > 0 && (
                    <>
                      <br />
                      • Erreurs: {importResult.errors.length}
                    </>
                  )}
                </div>
              </div>

              {/* Erreurs détaillées */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mb-3">
                  <h5>❌ Erreurs détectées:</h5>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}>
                    {importResult.errors.map((error: string, index: number) => (
                      <div key={index} style={{ fontSize: '0.9em', marginBottom: '0.25rem' }}>
                        • {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Questions importées */}
              {importResult.questions && importResult.questions.length > 0 && (
                <div className="mb-3">
                  <h5>✅ Questions importées:</h5>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}>
                    {importResult.questions.map((question: any, index: number) => (
                      <div key={index} style={{ fontSize: '0.9em', marginBottom: '0.25rem' }}>
                        • {question.title} ({question.type})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          {!importResult ? (
            <>
              <button 
                className="btn btn-outline"
                onClick={onCancel}
                disabled={loading}
              >
                Annuler
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleImport}
                disabled={!selectedFile || loading}
                style={{ flex: 1 }}
              >
                {loading ? (
                  <>
                    <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                    Import...
                  </>
                ) : (
                  '📥 Importer'
                )}
              </button>
            </>
          ) : (
            <button 
              className="btn btn-secondary"
              onClick={() => {
                onImportSuccess();
                onCancel();
              }}
              style={{ width: '100%' }}
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportQuestions;
